import React, { useEffect, useMemo, useState } from "react";
import RejectionAudit from "../job-details/RejectionAudit";
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { formatLocalDateTime } from "../../utils/dateTime";
import { RECORDS_CHANGED } from "../../utils/recordChanges";
import { pipelineStageKey, stageColor } from "../../utils/pipelineStages";

export default function CandidateRecruitingHistory({ candidateId, refreshKey, loadHistory }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [job, setJob] = useState("all");
  const [retry, setRetry] = useState(0);
  useEffect(() => { setJob("all"); setHistory([]); }, [candidateId]);
  useEffect(() => {
    if (!candidateId) { setLoading(false); return; }
    let active = true, requestId = 0;
    const load = async () => {
      const id = ++requestId;
      setLoading(true);
      try {
        const result = await loadHistory(candidateId);
        if (active && id === requestId) { setHistory(result.history || []); setError(""); }
      } catch (err) { if (active && id === requestId) setError("Unable to load recruiting history."); }
      finally { if (active && id === requestId) setLoading(false); }
    };
    load(); window.addEventListener(RECORDS_CHANGED, load);
    return () => { active = false; window.removeEventListener(RECORDS_CHANGED, load); };
  }, [candidateId, refreshKey, loadHistory, retry]);
  return <RecruitingHistoryContent history={history} loading={loading} error={error} onRetry={() => setRetry(value => value + 1)} job={job} setJob={setJob} />;
}

export function RecruitingHistoryContent({ history = [], loading, error, onRetry, stage, job = "all", setJob, showCandidate = false }) {
  const jobs = useMemo(() => [...new Map(history.filter(entry => entry.job_id).map(entry => [String(entry.job_id), entry.job_title || "Job"]))], [history]);
  const entries = history.filter(entry => (!stage || pipelineStageKey(entry.stage) === pipelineStageKey(stage)) && (job === "all" || String(entry.job_id || "profile") === job));
  return <Box component="section" aria-label="Recruiting History" sx={{ pt: 2, pb: 3, borderBottom: "1px solid #EAECF0" }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
      <Box><Typography variant="h6">Recruiting History</Typography>
        <Typography variant="body2" color="text.secondary">Recorded stage changes, from earliest to latest.</Typography></Box>
      {!!history.length && setJob && <TextField select size="small" label="History for" value={job} onChange={event => setJob(event.target.value)} sx={{ minWidth: 180 }}>
        <MenuItem value="all">All recruiting history</MenuItem>
        <MenuItem value="profile">Candidate stage</MenuItem>
        {jobs.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
      </TextField>}
    </Stack>
    {loading ? <CircularProgress size={22} aria-label="Loading recruiting history" sx={{ mt: 2 }} /> : error ?
      <Alert severity="error" sx={{ mt: 2 }} action={<Button onClick={onRetry}>Retry</Button>}>{error}</Alert> :
      !entries.length ? <Typography color="text.secondary" sx={{ mt: 2 }}>No recruiting stage changes have been recorded{job === "all" ? " yet" : " for this selection"}.</Typography> :
      <Box component="ol" sx={{ m: 0, mt: 2, pl: 3, maxHeight: 420, overflowY: "auto" }}>
        {entries.map(entry => <Box component="li" key={entry.id} sx={{ pl: 1, pb: 2, "&::marker": { color: stageColor(entry.stage) } }}>
          <Typography fontWeight={600} sx={{ color: stageColor(entry.stage) }}>{entry.stage === "Interview" ? "Interview stage" : entry.stage} on {formatLocalDateTime(entry.occurred_at, {}, "date unavailable")}</Typography>
          <Typography variant="body2" color="text.secondary">{showCandidate && entry.candidate_name ? `${entry.candidate_name} ? ` : ""}{entry.job_id ? entry.job_title || "Job" : "Candidate stage"}{entry.company_name ? ` · ${entry.company_name}` : ""}</Typography>
          <Typography variant="caption" color="text.secondary">{entry.previous_stage ? `From ${entry.previous_stage}` : "Initial recorded stage"}{entry.actor_name ? ` · ${entry.actor_name}` : ""}</Typography>
          {entry.outcome === "Rejected" && <RejectionAudit item={entry} />}
        </Box>)}
      </Box>}
  </Box>;
}
